<template>
  <PriceCompareLayout>
    <BreadcrumbBar :breadcrumbs="breadcrumbs" />
    <div class="container-fluid py-4">
      <div class="row">
        <!-- Main content column -->
        <div class="col-lg-9">
          <!-- Hero section -->
          <div class="hero-card mb-4">
            <div class="hero-content p-4">
              <div class="hero-decoration"></div>
              <div class="d-flex align-items-center flex-wrap gap-3">
                <div class="retailer-logo-box">
                  <img
                    v-if="retailer.logo"
                    :src="retailerLogoUrl(retailer.logo)"
                    :alt="retailer.name + ' logo'"
                    class="retailer-logo-img"
                  />
                  <div v-else class="retailer-logo-fallback">{{ retailer.name.charAt(0) }}</div>
                </div>
                <div class="flex-grow-1">
                  <h1 class="h3 fw-bold mb-1">{{ retailer.name }}</h1>
                  <div class="d-flex align-items-center flex-wrap gap-2">
                    <span v-if="retailer.group" class="badge bg-primary bg-opacity-25 text-primary">{{ retailer.group.name }}</span>
                    <!--
                      Den der googler butikkens navn og lander her vil ind i
                      butikken. Foer stod adressen som et lille graat link
                      uden sporing: ni butikssider gav 1.537 eksponeringer og
                      NUL klik i august 2026. Nu er det en handling med et
                      verbum, og gaar den gennem vores egen rute, tjener vi
                      paa den.
                    -->
                    <a v-if="butikslink" :href="butikslink" target="_blank" rel="nofollow sponsored noopener"
                       class="btn btn-sm btn-primary d-inline-flex align-items-center fw-semibold">
                      <i class="bi bi-box-arrow-up-right me-1"></i>
                      Gå til {{ retailer.name }}
                    </a>
                    <!--
                      Kolonnen hedder website_url. Her stod retailer.website,
                      som ikke findes, saa knappen i toppen blev ALDRIG vist.
                      Kun det lille link i infoboksen laengere nede.
                    -->
                    <a v-else-if="retailer.website_url" :href="retailer.website_url" target="_blank" rel="nofollow noopener noreferrer"
                       class="btn btn-sm btn-outline-light d-inline-flex align-items-center">
                      <i class="bi bi-box-arrow-up-right me-1"></i>
                      {{ formatURL(retailer.website_url) }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- About + Info combined -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-body p-4">
              <div class="row">
                <div class="col-md-7">
                  <h2 class="h5 mb-3">Om {{ retailer.name }}</h2>
                  <p v-if="retailer.description" class="mb-0" style="color: var(--pc-text-secondary);">
                    {{ retailer.description }}
                  </p>
                  <p v-else class="text-muted fst-italic mb-0">
                    Der er endnu ikke tilføjet en beskrivelse for denne forhandler.
                  </p>
                </div>
                <div class="col-md-5 mt-3 mt-md-0">
                  <div class="info-grid">
                    <div v-if="retailer.website_url" class="info-item">
                      <i class="bi bi-globe2"></i>
                      <div>
                        <small class="text-muted d-block">Hjemmeside</small>
                        <a :href="butikslink || retailer.website_url" target="_blank"
                           :rel="butikslink ? 'nofollow sponsored noopener' : 'nofollow noopener noreferrer'"
                           class="text-decoration-none small">
                          {{ formatURL(retailer.website_url) }}
                        </a>
                      </div>
                    </div>
                    <div v-if="retailer.email" class="info-item">
                      <i class="bi bi-envelope"></i>
                      <div>
                        <small class="text-muted d-block">Email</small>
                        <a :href="`mailto:${retailer.email}`" class="text-decoration-none small">{{ retailer.email }}</a>
                      </div>
                    </div>
                    <div v-if="retailer.phone" class="info-item">
                      <i class="bi bi-telephone"></i>
                      <div>
                        <small class="text-muted d-block">Telefon</small>
                        <a :href="`tel:${retailer.phone}`" class="text-decoration-none small">{{ retailer.phone }}</a>
                      </div>
                    </div>
                    <div v-if="retailer.founded_date" class="info-item">
                      <i class="bi bi-calendar-event"></i>
                      <div>
                        <small class="text-muted d-block">Grundlagt</small>
                        <span class="small">{{ foundedYear }}</span>
                      </div>
                    </div>
                    <div v-if="retailer.employees" class="info-item">
                      <i class="bi bi-people"></i>
                      <div>
                        <small class="text-muted d-block">Ansatte</small>
                        <span class="small">{{ retailer.employees }}</span>
                      </div>
                    </div>
                    <div v-if="retailer.industry" class="info-item">
                      <i class="bi bi-briefcase"></i>
                      <div>
                        <small class="text-muted d-block">Branche</small>
                        <span class="small">{{ retailer.industry }}</span>
                      </div>
                    </div>
                    <div v-if="retailer.cvr" class="info-item">
                      <i class="bi bi-building"></i>
                      <div>
                        <small class="text-muted d-block">CVR</small>
                        <span class="small">{{ retailer.cvr }}</span>
                      </div>
                    </div>
                    <div v-if="retailer.address" class="info-item">
                      <i class="bi bi-geo-alt"></i>
                      <div>
                        <small class="text-muted d-block">Adresse</small>
                        <span class="small">{{ retailer.address }}<template v-if="retailer.zip">, {{ retailer.zip }} {{ retailer.city }}</template></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Popular Products -->
          <div v-if="popularProducts && popularProducts.length > 0" class="card border-0 shadow-sm mb-4">
            <div class="card-body">
              <ProductCarousel
                headline="Populære produkter"
                tagline="Produkter hvor denne forhandler har den bedste pris"
                :products="popularProducts"
                :productsPrPage="4"
                :showMoreLinkAnchor="'/products?retailer=' + retailer.id"
                :componentIdentifier="'popularProductsCarousel'"
              />
            </div>
          </div>

          <!-- Categories grid -->
          <div v-if="retailerCategories && retailerCategories.length > 0" class="card border-0 shadow-sm mb-4">
            <div class="card-body p-4">
              <h3 class="h5 mb-3">
                <i class="bi bi-grid me-2" style="color: var(--pc-primary);"></i>Produktkategorier
              </h3>
              <div class="row g-2">
                <div v-for="category in retailerCategories" :key="category.id" class="col-6 col-md-4 col-lg-3">
                  <Link :href="category.url" class="text-decoration-none">
                    <div class="category-chip">
                      <i class="bi bi-tag me-1"></i>
                      {{ category.name }}
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="col-lg-3 mt-4 mt-lg-0">
          <!-- Payment Options -->
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-body p-3">
              <h4 class="h6 fw-bold mb-3">
                <i class="bi bi-credit-card me-2" style="color: var(--pc-primary);"></i>Betalingsmuligheder
              </h4>
              <div v-if="retailer.payment_options && retailer.payment_options.length > 0" class="d-flex flex-wrap gap-2">
                <div v-for="payment in retailer.payment_options" :key="payment.id" class="icon-card" v-tooltip="payment.payment_name">
                  <div class="icon-card-img-wrap">
                    <img v-if="payment.payment_logo_image" :src="payment.payment_logo_image" :alt="payment.payment_name" />
                  </div>
                  <small>{{ payment.payment_name }}</small>
                </div>
              </div>
              <p v-else class="text-muted small fst-italic mb-0">Ingen oplyst</p>
            </div>
          </div>

          <!-- Carrier Options -->
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-body p-3">
              <h4 class="h6 fw-bold mb-3">
                <i class="bi bi-truck me-2" style="color: var(--pc-primary);"></i>Forsendelse
              </h4>
              <div v-if="retailer.carrier_options && retailer.carrier_options.length > 0" class="d-flex flex-wrap gap-2">
                <div v-for="carrier in retailer.carrier_options" :key="carrier.id" class="icon-card" v-tooltip="carrier.carrier_name">
                  <div class="icon-card-img-wrap">
                    <img v-if="carrier.carrier_logo_image" :src="carrier.carrier_logo_image" :alt="carrier.carrier_name" />
                  </div>
                  <small>{{ carrier.carrier_name }}</small>
                </div>
              </div>
              <p v-else class="text-muted small fst-italic mb-0">Ingen oplyst</p>
            </div>
          </div>

          <!-- Trust marks / certifications -->
          <div v-if="trustMarks.length > 0" class="card border-0 shadow-sm mb-3">
            <div class="card-body p-3">
              <h4 class="h6 fw-bold mb-3">
                <i class="bi bi-shield-check me-2" style="color: var(--pc-primary);"></i>Certificeringer &amp; mærker
              </h4>
              <div class="d-flex flex-wrap gap-2">
                <span v-for="mark in trustMarks" :key="mark.key" class="sidebar-badge" :title="mark.label">
                  <i :class="mark.icon" class="me-1"></i>{{ mark.label }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PriceCompareLayout>
</template>

<script setup>
import { computed } from 'vue';
import { retailerLogoUrl } from '@/Utils/retailerLogo';
import { Link } from '@inertiajs/vue3';
import PriceCompareLayout from '@/Layouts/PriceCompareLayout.vue';
import ProductCarousel from '@/Components/Carousels/ProductCarousel.vue';
import BreadcrumbBar from '@/Components/BreadcrumbBar.vue';

const props = defineProps({
  breadcrumbs: {
    type: Array,
    default: () => []
  },
  retailer: Object,
  // Sporet link til butikkens forside. Null naar vi ikke har en aftale med
  // dem, og saa staar det almindelige link som foer.
  butikslink: {
    type: String,
    default: null
  },
  popularProducts: {
    type: Array,
    default: () => []
  },
  retailerCategories: {
    type: Array,
    default: () => []
  }
});

const formatURL = (url) => {
  if (!url) return '';
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
};

// Founding year from the CVR founded_date (SSR-safe: parse a given string).
const foundedYear = computed(() => {
  const d = props.retailer?.founded_date;
  if (!d) return '';
  const m = String(d).match(/(\d{4})/);
  return m ? m[1] : '';
});

// Trust marks the retailer holds, as display badges.
const trustMarks = computed(() => {
  const r = props.retailer || {};
  return [
    { key: 'emaerket', on: r.has_mark_emaerket, label: 'e-mærket', icon: 'bi bi-patch-check' },
    { key: 'dmaerket', on: r.has_mark_dmaerket, label: 'D-mærket', icon: 'bi bi-shield-lock' },
    { key: 'ingenco2', on: r.has_mark_ingenco2, label: 'CO2-neutral levering', icon: 'bi bi-truck' },
    { key: 'miljoepakning', on: r.has_mark_miljoepakning, label: 'CO2-neutral emballage', icon: 'bi bi-box-seam' },
    { key: 'csr', on: r.has_mark_csr, label: 'CSR', icon: 'bi bi-people' },
  ].filter((m) => m.on);
});
</script>

<style scoped>
.hero-card {
  position: relative;
  background: linear-gradient(135deg, var(--pc-bg-secondary) 0%, var(--pc-bg-tertiary) 100%);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: var(--pc-shadow);
  border-left: 4px solid var(--pc-primary, #00c9a7);
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero-decoration {
  position: absolute;
  top: 0;
  right: 0;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle at top right, color-mix(in srgb, var(--pc-primary, #00c9a7) 10%, transparent) 0%, transparent 70%);
  z-index: 1;
}

.retailer-logo-box {
  width: 80px;
  height: 60px;
  background: #fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  flex-shrink: 0;
}

.retailer-logo-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.retailer-logo-fallback {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--pc-text-muted, #6c757d);
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.info-item i {
  color: var(--pc-primary, #00c9a7);
  font-size: 1rem;
  margin-top: 2px;
  flex-shrink: 0;
}

.category-chip {
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: var(--pc-bg-secondary, #1a1d23);
  border: 1px solid var(--pc-border, rgba(255, 255, 255, 0.1));
  color: var(--pc-text-primary, #e4e6ea);
  font-size: 0.8rem;
  transition: all 0.15s ease;
  text-align: center;
}

.category-chip:hover {
  border-color: var(--pc-primary, #00c9a7);
  color: var(--pc-primary, #00c9a7);
  transform: translateY(-1px);
}

.sidebar-badge {
  display: inline-block;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  background: var(--pc-bg-secondary, #1a1d23);
  border: 1px solid var(--pc-border, rgba(255, 255, 255, 0.1));
  color: var(--pc-text-secondary, #adb5bd);
  font-size: 0.75rem;
}

.icon-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 90px;
  text-align: center;
}

.icon-card small {
  font-size: 0.65rem;
  color: var(--pc-text-muted, #6c757d);
  line-height: 1.2;
}

.icon-card-img-wrap {
  width: 80px;
  height: 48px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.icon-card-img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

@media (max-width: 767.98px) {
  .hero-card h1 {
    font-size: 1.4rem;
  }

  .retailer-logo-box {
    width: 60px;
    height: 45px;
  }

  .info-grid {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--pc-border, rgba(255, 255, 255, 0.1));
  }

  .category-chip {
    font-size: 0.75rem;
    padding: 0.4rem 0.5rem;
  }
}
</style>
